import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// 导入本地图片资源
import banner1 from '@/assest/banner/banner1.png';
import banner2 from '@/assest/banner/banner2.png';
import banner3 from '@/assest/banner/banner3.png';
import banner4 from '@/assest/banner/banner4.png';

interface BannerSlide {
  id: number;
  image: string;
  title?: string;
  subtitle?: string;
}

interface BannerProps {
  simple?: boolean;
}

const slides: BannerSlide[] = [
  {
    id: 1,
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/index-bg1.png',
    title: '互联网生态平台',
    subtitle: '构建数字化商业生态，助力企业转型升级',
  },
  {
    id: 2,
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/index-bg2.png',
    title: '大宗商品交易',
    subtitle: '连接供需双方，打造高效供应链平台',
  },
  {
    id: 3,
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/index-bg3.png',
    title: '数字化营销',
    subtitle: '精准触达目标用户，提升营销ROI',
  },
];

// 使用导入的图片变量
const slideShop: BannerSlide[] = [
  {
    id: 1,
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/shop_poster1.png',
  },
  {
    id: 2,
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/shop_poster2.png',
  },
  {
    id: 3,
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/shop_poster3.png',
  },
  {
    id: 4,
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/shop_poster4.png',
  },
  {
    id: 5,
    image: 'https://www.cdkmzs.com/uploads/platform/0703/cdkmzs/shop_poster5.png',
  },
];

export default function Banner({ simple = false }: BannerProps) {
  // 根据 simple 属性动态选择数据源，简化后续逻辑
  const currentSlides = simple ? slideShop : slides;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 800);
  }, [isAnimating]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % currentSlides.length);
  }, [currentSlide, goToSlide, currentSlides.length]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + currentSlides.length) % currentSlides.length);
  }, [currentSlide, goToSlide, currentSlides.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  // 根据 simple 模式定义按钮样式
  const buttonClass = simple
    ? "absolute top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/50 transition-all duration-300 group shadow-lg border border-white/10"
    : "absolute top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 group";

  return (
    <section className={`relative w-full ${simple ? 'h-[400px] md:h-[500px] lg:h-[600px]' : 'h-[700px] md:h-[800px] lg:h-[900px]'} overflow-hidden`}>
      {/* Slides */}
      {currentSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-all duration-800 ${
            index === currentSlide
              ? 'opacity-100 scale-100'
              : 'opacity-0 scale-105'
          }`}
          style={{
            transitionTimingFunction: 'var(--ease-expo-out)',
          }}
        >
          <div
            className="absolute inset-0 bg-center"
            style={{ 
              backgroundImage: `url(${slide.image})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat'
            }}
          />
          {/* 根据 simple 模式设置不同的遮罩 */}
           <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
          {/* {simple ? (
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          )} */}
        </div>
      ))}

      {/* Content - 仅在非 simple 模式下显示 */}
      {!simple && (
        <div className="relative h-full section-container flex items-center">
          <div className="max-w-2xl">
            {slides.map((slide, index) => (
              <div
                key={slide.id}
                className={`transition-all duration-700 ${
                  index === currentSlide
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8 absolute'
                }`}
                style={{
                  transitionTimingFunction: 'var(--ease-expo-out)',
                  transitionDelay: index === currentSlide ? '200ms' : '0ms',
                }}
              >
                {index === currentSlide && (
                  <>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                      {slide.title}
                    </h1>
                    <p className="text-xl md:text-2xl text-white/90 mb-8">
                      {slide.subtitle}
                    </p>
                    {/* <div className="flex gap-4">
                      <button className="btn-primary">
                        立即咨询
                      </button>
                      <button className="px-6 py-3 rounded-lg border-2 border-white text-white font-semibold hover:bg-white hover:text-black transition-all duration-300">
                        了解更多
                      </button>
                    </div> */}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className={`${buttonClass} left-4 md:left-8`}
        disabled={isAnimating}
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 group-hover:-translate-x-0.5 transition-transform" />
      </button>
      <button
        onClick={nextSlide}
        className={`${buttonClass} right-4 md:right-8`}
        disabled={isAnimating}
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 group-hover:translate-x-0.5 transition-transform" />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
        {currentSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all duration-500 ${
              index === currentSlide
                ? 'w-8 bg-[#1c5bff]'
                : 'w-2 bg-white/70 hover:bg-white'
            }`}
            style={{
              transitionTimingFunction: 'var(--ease-expo-out)',
            }}
          />
        ))}
      </div>

      {/* Decorative Element */}
      <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#1c5bff]/20 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
}